import { IconCheck } from '@tabler/icons-react-native';
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Platform, Pressable } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type CheckboxSize = 'small' | 'large';
export type CheckboxVariant = 'squared' | 'rounded';
export type CheckboxState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

export interface CheckboxProps {
  label?: string;
  description?: string;
  showDescription?: boolean;
  showText?: boolean;
  selected?: boolean;
  /** The current Figma family uses small; large is retained for existing consumers. */
  size?: CheckboxSize;
  variant?: CheckboxVariant;
  inverted?: boolean;
  /** Optional visual state for documentation; disabled always suppresses activation. */
  state?: CheckboxState;
  disabled?: boolean;
  fullWidth?: boolean;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  testID?: string;
  onPress?: () => void;
  onValueChange?: (selected: boolean) => void;
}

type CheckboxTheme = ReturnType<typeof useTheme>;
type ColorToken = keyof CheckboxTheme['colors'];

type PresentationProps = Required<Pick<CheckboxProps, 'variant' | 'inverted' | 'selected' | 'showText'>> & {
  disabled: boolean;
  hovered: boolean;
  pressed: boolean;
};

function getPresentation({ variant, inverted, selected, showText, disabled, hovered, pressed }: PresentationProps) {
  const rounded = variant === 'rounded';
  const active = !disabled && (hovered || pressed);
  const border: ColorToken = disabled
    ? inverted
      ? 'border.inverse.disabled'
      : 'border.disabled'
    : active
      ? inverted
        ? rounded && !showText
          ? 'border.inverse.interactive'
          : 'border.inverse.selected'
        : 'border.interactive'
      : inverted
        ? 'border.inverse.primary'
        : 'border.primary';
  const background: ColorToken | undefined = selected
    ? disabled
      ? inverted
        ? 'surface.inverse.disabled'
        : 'surface.disabled'
      : inverted
        ? 'surface.inverse.selected-strong'
        : rounded && !showText
          ? 'surface.selected-strong'
          : 'surface.interactive'
    : !showText && (inverted || (rounded && disabled))
      ? undefined
      : 'surface.default';
  const icon: ColorToken = disabled
    ? inverted
      ? 'icon.inverse.disabled'
      : 'icon.disabled'
    : inverted
      ? 'icon.inverse.on-selected'
      : 'icon.inverse.primary';
  const label: ColorToken = disabled
    ? inverted
      ? 'text.inverse.disabled'
      : 'text.disabled'
    : inverted
      ? 'text.inverse.primary'
      : 'text.primary';
  const description: ColorToken = disabled ? label : inverted ? 'text.inverse.secondary' : 'text.secondary';
  const outerLayer: ColorToken | undefined = active
    ? pressed
      ? 'state-layer.press-brand'
      : 'state-layer.hover-brand'
    : undefined;
  // Figma uses inverse layers for bare rounded controls and inverted squared controls.
  const inverseLayer = rounded ? !showText : inverted;
  const controlLayer: ColorToken | undefined = active
    ? inverseLayer
      ? pressed && !(inverted && selected && !rounded && !showText)
        ? 'state-layer.inverse.press-brand'
        : 'state-layer.inverse.hover-brand'
      : outerLayer
    : undefined;
  return { border, background, icon, label, description, outerLayer, controlLayer };
}

export function Checkbox({
  label = 'Checkbox label',
  description = 'Description',
  showDescription = false,
  showText = true,
  selected = false,
  size = 'small',
  variant = 'squared',
  inverted = false,
  state = 'enabled',
  disabled = false,
  fullWidth = false,
  accessibilityLabel,
  accessibilityHint,
  testID,
  onPress,
  onValueChange,
}: CheckboxProps) {
  const theme = useTheme();
  const descriptionId = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const spaceKeyDown = useRef(false);
  const isDisabled = disabled || state === 'disabled';
  const hasDescription = showText && showDescription && Boolean(description);
  const controlSize = Number(theme.tokens.sizing['sizing.general.x6']);
  const iconSize = Number(theme.tokens.sizing['sizing.icon.sm']);
  const borderWidth = selected ? 0 : theme.borderWidths['border-width.subtle'];
  const radius = variant === 'rounded' ? 'radius.full' : 'radius.xs';
  const focusInset = -theme.spacing['spacing.general.x1'] - borderWidth;

  function handlePress() {
    if (isDisabled) return;
    onValueChange?.(!selected);
    onPress?.();
  }

  // RN Web's Pressable only handles Space for the button role; Enter is already supported.
  const webKeyboardProps =
    Platform.OS === 'web'
      ? {
          onKeyDown(event: KeyboardEvent<HTMLElement>) {
            if (event.key !== ' ') return;
            event.preventDefault();
            if (!isDisabled && !event.repeat) {
              spaceKeyDown.current = true;
              setSpacePressed(true);
            }
          },
          onKeyUp(event: KeyboardEvent<HTMLElement>) {
            if (event.key !== ' ') return;
            event.preventDefault();
            if (spaceKeyDown.current) handlePress();
            spaceKeyDown.current = false;
            setSpacePressed(false);
          },
        }
      : {};

  return (
    <Pressable
      {...webKeyboardProps}
      testID={testID}
      accessible
      accessibilityRole="checkbox"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint ?? (hasDescription ? description : undefined)}
      accessibilityState={{ checked: selected, disabled: isDisabled }}
      aria-checked={selected}
      aria-disabled={isDisabled}
      aria-describedby={Platform.OS === 'web' && hasDescription ? descriptionId : undefined}
      disabled={isDisabled}
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        spaceKeyDown.current = false;
        setSpacePressed(false);
      }}
      hitSlop={theme.spacing['spacing.general.x3']}
      style={{
        alignSelf: fullWidth ? 'stretch' : 'flex-start',
        maxWidth: '100%',
        ...(Platform.OS === 'web' ? { outlineWidth: 0, outlineStyle: 'solid' as const } : {}),
      }}
    >
      {({ pressed }) => {
        const presentation = getPresentation({
          variant,
          inverted,
          selected,
          showText,
          disabled: isDisabled,
          hovered: state === 'hover' || (state === 'enabled' && hovered),
          pressed: state === 'pressed' || (state === 'enabled' && (pressed || spacePressed)),
        });
        const showFocus = !isDisabled && (focused || state === 'focus');
        return (
          <Box flexDirection="row" alignItems="flex-start" gap="spacing.general.x4" position="relative">
            {showText && presentation.outerLayer ? (
              <Box
                pointerEvents="none"
                position="absolute"
                top={-theme.spacing['spacing.general.x2']}
                bottom={-theme.spacing['spacing.general.x2']}
                left={-theme.spacing['spacing.general.x4']}
                right={-theme.spacing['spacing.general.x4']}
                backgroundColor={presentation.outerLayer}
              />
            ) : null}
            <Box
              width={controlSize}
              height={controlSize}
              flexShrink={0}
              borderRadius={radius}
              borderWidth={borderWidth}
              borderColor={presentation.border}
              backgroundColor={presentation.background}
              alignItems="center"
              justifyContent="center"
            >
              {selected ? <IconCheck size={iconSize} color={theme.colors[presentation.icon]} strokeWidth={2} /> : null}
              {presentation.controlLayer ? (
                <Box
                  pointerEvents="none"
                  position="absolute"
                  top={-borderWidth}
                  bottom={-borderWidth}
                  left={-borderWidth}
                  right={-borderWidth}
                  borderRadius={radius}
                  backgroundColor={presentation.controlLayer}
                />
              ) : null}
              {showFocus ? (
                <Box
                  pointerEvents="none"
                  position="absolute"
                  top={focusInset}
                  bottom={focusInset}
                  left={focusInset}
                  right={focusInset}
                  borderWidth={theme.borderWidths['border-width.emphasized']}
                  borderColor="border.focused"
                  borderRadius={variant === 'rounded' ? 'radius.full' : 'radius.sm'}
                />
              ) : null}
            </Box>
            {showText ? (
              <Box
                minHeight={controlSize}
                minWidth={0}
                justifyContent="center"
                gap="spacing.general.x1"
                // Figma's label column is an untokenized 113px; fullWidth lets content use its parent's width.
                width={fullWidth ? undefined : size === 'large' ? 128 : 113}
                flex={fullWidth ? 1 : undefined}
                flexShrink={1}
              >
                <Text variant={size === 'large' ? 'bodyLargeMedium' : 'bodyBase'} color={presentation.label}>
                  {label}
                </Text>
                {hasDescription ? (
                  <Text nativeID={descriptionId} variant="bodySmall" color={presentation.description}>
                    {description}
                  </Text>
                ) : null}
              </Box>
            ) : null}
          </Box>
        );
      }}
    </Pressable>
  );
}
